import type { InitiatePayinResponse } from "../services/paymentsPayin.service";
import type { PayinData } from "../types/payins.types";

/** Brazil-specific extra fields the success screen reads to render the PIX QR. */
export type AdaptedPayinData = PayinData & {
  copia_e_cola?: string;
  payload_b64?: string;
  amount_brl?: number;
  expired_at?: string;
  qr_content?: string;
};

const EMPTY_PAYER_FIELDS = {
  payer_name: "",
  payment_method: "",
  payer_document_type: "",
  payer_document: "",
  payer_email: "",
  payer_phone_number: "",
  pay_url: "",
};

/**
 * Adapts the NEW payments-runtime Brazil response (`InitiatePayinResponseDto`) to the shape
 * `PayinSuccessScreen` expects. The runtime returns the EMV / copia-e-cola string (`qr_code_payload`), not a
 * PNG — the caller renders the QR client-side (qrcode lib) and passes it as `qrDataUrl` so both the screen
 * and the PDF reuse the existing `payload_b64` path. Money fields arrive as 8 dp decimal strings. The
 * commission zeros are placeholders for the required `PayinData.commission` shape — the runtime only reports
 * `fee_total`, and the screen hides the commission block for Brazil anyway.
 */
export function adaptRuntimeBrazilPayin(
  payin: InitiatePayinResponse,
  qrDataUrl: string | null,
): AdaptedPayinData {
  const now = new Date().toISOString();
  return {
    ...EMPTY_PAYER_FIELDS,
    id: payin.id,
    // Lowercased for the screen's status handling; CREATED counts as pending (webhook settles it).
    status: payin.status.toLowerCase(),
    amount: Number(payin.gross_amount),
    amount_received: Number(payin.net_amount),
    commission: { percentage: "0", fee_fix: "0", total_fee: payin.fee_total },
    created_at: now,
    updated_at: now,
    copia_e_cola: payin.qr_code_payload ?? undefined,
    payload_b64: qrDataUrl ?? undefined,
    amount_brl: Number(payin.gross_amount),
    expired_at: payin.expires_at ?? undefined,
  };
}
